import { FC, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useAppProviderContext } from '../../../core/application/contexts'
import { CreateDropoffLocationErrors } from '../../../core/application/formValidation/createDropoffFormValidator'
import { useDebounce } from '../../../core/application/hooks'
import { AddressResult } from '../../../core/domain/providers/IGeocodingProvider'
import AddressSuggestionList from '../../shared/AddressSuggestionList'
import DropdownWrapper from '../../shared/DropDownWrapper'
import InputContainer, { InputContainerBackground } from '../inputs/InputContainer'
import TextInput from '../inputs/TextInput'

interface AddressSearchInputProps {
  errors?: CreateDropoffLocationErrors
  editMapLocation: boolean
  onSelect: (address: AddressResult) => void
  value: string
  onChange: (e: string) => void
  placeholder?: string
}

const AddressSearchInput: FC<AddressSearchInputProps> = (props) => {
  const { errors, editMapLocation, onSelect, onChange, value, placeholder } = props
  const [suggestions, setSuggestions] = useState<AddressResult[]>([])
  const { geocodingProvider } = useAppProviderContext()
  const [displayList, setDisplayList] = useState<boolean>(false)

  const debouncedAddress = useDebounce(value, 500)

  useEffect(() => {
    if (!geocodingProvider) {
      return
    }
    if (!editMapLocation && debouncedAddress.length > 2) {
      geocodingProvider.getAddressFromAddress(debouncedAddress).then((result) => {
        setSuggestions(result)
      })
    }
  }, [debouncedAddress, geocodingProvider, editMapLocation])

  const onFocus = () => {
    setDisplayList(true)
  }

  const onBlur = useCallback(() => {
    setTimeout(() => {
      setDisplayList(false)
    }, 400)
  }, [])

  const onSelectSuggestion = useCallback(
    (suggestion: AddressResult) => {
      onSelect(suggestion)
      setDisplayList(false)
    },
    [onSelect]
  )

  return (
    <Container>
      <InputContainer
        background={InputContainerBackground.transparent}
        errorIntlKey={errors?.address}
        borderColor={editMapLocation ? { default: 'rgba(0,0,0,0)' } : undefined}
      >
        <TextInput
          value={value}
          readonly={editMapLocation}
          disabled={editMapLocation}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          id="address"
        />
      </InputContainer>
      <DropdownWrapper open={displayList}>
        <AddressSuggestionList suggestions={suggestions} onSelect={onSelectSuggestion} />
      </DropdownWrapper>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  width: 100%;
`

export default AddressSearchInput
